<script lang="ts" setup>
import {
  AddressBookBackup,
  AddressBookBundle,
  useAddressBookStore,
} from '@/store/addressBook';
import { ref } from 'vue';
import useBackupChecker from './useBackupChecker';
import { snackbarPush } from '@/helpers/snackbarPush';
import useBackupExport from './useBackupExport';
import { getAddressSystem } from '@sonarwatch/portfolio-core';

const store = useAddressBookStore();
const fileInput = ref<HTMLInputElement | null>(null);
const { check } = useBackupChecker();
const dialogOpened = ref(false);
const backupData = ref<AddressBookBackup | null>(null);
const { prepareBackup } = useBackupExport();

function handleClick() {
  fileInput.value?.click();
}

function mergeBackup() {
  if (!backupData.value) {
    snackbarPush('No backup data to import.');
    return;
  }
  const current = prepareBackup();
  const merged = mergeAddressBooks(current, backupData.value);
  const cleared = clearNotSolanaAddresses(merged);
  store.importBundles(cleared);
  snackbarPush('Backup merged.');
  dialogOpened.value = false;
  backupData.value = null;
}

function mergeAddressBooks(
  book1: AddressBookBackup,
  book2: AddressBookBackup,
): AddressBookBackup {
  // Merge items, avoiding duplicates based on `address`
  const mergedItems = [
    ...book1.items,
    ...book2.items.filter(
      (item2) => !book1.items.some((item1) => item1.address === item2.address),
    ),
  ];

  // Merge bundles, avoiding duplicates based on `id` and updating existing bundles
  const mergedBundlesMap = new Map<string, AddressBookBundle>();

  // Add all bundles from book1
  for (const bundle of book1.bundles) {
    mergedBundlesMap.set(bundle.id, { ...bundle });
  }

  // Merge bundles from book2
  for (const bundle of book2.bundles) {
    if (mergedBundlesMap.has(bundle.id)) {
      // Update the existing bundle
      const existingBundle = mergedBundlesMap.get(bundle.id)!;
      const mergedItems = [
        ...existingBundle.items,
        ...bundle.items.filter(
          (item2) =>
            !existingBundle.items.some(
              (item1) => item1.address === item2.address,
            ),
        ),
      ];
      mergedBundlesMap.set(bundle.id, {
        ...existingBundle,
        items: mergedItems,
      });
    } else {
      // Add the new bundle
      mergedBundlesMap.set(bundle.id, { ...bundle });
    }
  }

  const mergedBundles = Array.from(mergedBundlesMap.values());

  return {
    items: mergedItems,
    bundles: mergedBundles,
    createdAt: Date.now(),
  };
}

function clearNotSolanaAddresses(backup: AddressBookBackup) {
  const solanaAddresses = backup.items.filter((item) => {
    const system = getAddressSystem(item.address);
    return system === 'solana';
  });
  const solanaBundles = backup.bundles.map((bundle) => {
    return {
      ...bundle,
      items: bundle.items.filter((item) => {
        const system = getAddressSystem(item.address);
        return system === 'solana';
      }),
    };
  });
  return {
    items: solanaAddresses,
    bundles: solanaBundles,
    createdAt: Date.now(),
  };
}

function importBackup() {
  if (!backupData.value) {
    snackbarPush('No backup data to import.');
    return;
  }
  const cleared = clearNotSolanaAddresses(backupData.value);
  store.importBundles(cleared);
  snackbarPush('Backup imported.');
  dialogOpened.value = false;
  backupData.value = null;
}

function loadFile(e: Event) {
  const input = e.target as HTMLInputElement;

  try {
    if (!input.files || input.files.length === 0) {
      throw new Error('No file selected.');
    }

    const file = input.files[0];

    if (file.type !== 'application/json') {
      throw new Error('Selected file is not a JSON file.');
    }

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const content = e.target?.result as string;
        const jsonData = JSON.parse(content);
        if (check(jsonData)) {
          backupData.value = jsonData;
          if (store.isEmpty) {
            importBackup();
          } else {
            dialogOpened.value = true;
          }
        } else {
          snackbarPush('Invalid backup file.');
        }
      } catch (error) {
        snackbarPush('Error loading file.');
      }
    };

    reader.onerror = () => {
      throw new Error('Error loading file.');
    };

    reader.readAsText(file);
  } catch (error) {
    snackbarPush((error as Error).message);
  } finally {
    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
}
</script>

<template>
  <v-dialog v-model="dialogOpened" max-width="700px">
    <v-card>
      <v-card-text>
        <v-container class="flex flex-col gap-4">
          <h4 class="text-lg font-bold">Import confirmation</h4>
          <p>
            Some addresses or bundles already exist in your address book. Please
            choose how you would like to handle the imported data:
          </p>
          <ul class="flex flex-col gap-1">
            <li>
              <v-icon class="mr-2">mdi-file-replace-outline</v-icon>
              <strong>Replace existing entries:</strong> This will overwrite the
              current addresses and bundles with the imported ones.
            </li>
            <li>
              <v-icon class="mr-2">mdi-merge</v-icon>
              <strong>Merge entries:</strong> The imported addresses and bundles
              will be added to your address book without removing any existing
              data.
            </li>
          </ul>
          <div class="flex flex-row gap-2 justify-end mt-4">
            <v-btn @click="dialogOpened = false" variant="plain">Cancel</v-btn>
            <v-btn @click="importBackup">
              <v-icon>mdi-file-replace-outline</v-icon>
              <span class="ml-2">Replace</span></v-btn
            >
            <v-btn @click="mergeBackup">
              <v-icon>mdi-merge</v-icon>
              <span class="ml-2">Merge</span>
            </v-btn>
          </div>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
  <v-btn @click="handleClick">
    <v-icon>mdi-file-import</v-icon>
    <span class="ml-2">Import</span>
    <input
      type="file"
      @change="loadFile"
      ref="fileInput"
      style="display: none"
    />
  </v-btn>
</template>
